import { ArrowUpRight, Check } from "lucide-react";
import ModalShell from "@/components/site/ModalShell";
import ProductLogoTile from "@/components/site/ProductLogoTile";

export default function ProductDetailsModal({ product, features, onClose }) {
  const { id, name, tagline, description, status, href, screenshot, ctaLabel } =
    product;
  const labelledBy = `product-details-title-${id}`;
  const publiclyAvailable = status.tone !== "neutral";

  return (
    <ModalShell
      onClose={onClose}
      labelledBy={labelledBy}
      closeLabel={`Close ${name} details`}
      maxWidthClassName="max-w-3xl"
    >
      <article data-testid={`product-details-modal-${id}`}>
        <header className="flex items-start gap-4 pr-14 sm:gap-5">
          <ProductLogoTile product={product} size={56} eager />
          <div className="min-w-0">
            <h2
              id={labelledBy}
              className="font-serif-display text-3xl font-medium text-cream sm:text-4xl"
            >
              {name}
            </h2>
            <p className="mt-1.5 text-sm text-white/70">{tagline}</p>
          </div>
        </header>

        <div className="mt-6">
          <span
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-medium uppercase tracking-[0.14em] ${
              publiclyAvailable
                ? "border-[#10B981]/30 bg-[#10B981]/10 text-[#10B981]"
                : "border-line-strong text-white/75"
            }`}
          >
            {publiclyAvailable ? (
              <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" />
            ) : null}
            {status.label}
          </span>
        </div>

        {screenshot ? (
          <figure className="relative mt-6 overflow-hidden rounded-md border border-line bg-surface-3">
            <img
              src={screenshot.src}
              alt={screenshot.alt}
              width={screenshot.width}
              height={screenshot.height}
              className="aspect-[2.83/1] w-full object-cover"
              style={{ objectPosition: screenshot.position }}
              decoding="async"
            />
            {screenshot.caption ? (
              <figcaption className="absolute bottom-2 right-2 max-w-[calc(100%-1rem)] rounded-sm bg-black/80 px-2 py-1 text-[10px] leading-relaxed text-white/90">
                {screenshot.caption}
              </figcaption>
            ) : null}
          </figure>
        ) : null}

        <p className="mt-6 max-w-[68ch] text-sm leading-relaxed text-white/80 sm:text-base">
          {description}
        </p>

        {features.length ? (
          <ul className="mt-6 divide-y divide-line border-y border-line">
            {features.map((feature) => (
              <li
                key={feature.term}
                className="grid grid-cols-[20px_1fr] gap-3 py-4"
              >
                <Check
                  size={16}
                  strokeWidth={1.75}
                  className="mt-0.5 text-[#10B981]"
                  aria-hidden="true"
                />
                <div>
                  <p className="text-sm font-semibold text-white">
                    {feature.term}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-white/70">
                    {feature.detail}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        ) : null}

        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`product-details-visit-${id}`}
            className="group mt-7 inline-flex min-h-[48px] items-center gap-2 rounded-full bg-[#10B981] px-6 py-3 text-sm font-semibold text-[#06231A] transition-colors hover:bg-[#34D399]"
          >
            {ctaLabel}
            <ArrowUpRight
              size={15}
              className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            />
          </a>
        ) : null}
      </article>
    </ModalShell>
  );
}
